'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Text } from '../../../../../components/styled/text';
import { MarkdownPreview } from '../../../../markdown-preview';
import { SparklesIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { getSkillDisplayNameFromPath } from '../skill-display';

type FileReadData = {
  filePath?: string;
  content?: string;
  contentLength?: number;
};
type FileReadRendererProps = {
  data: FileReadData;
  maxHeight?: string;
};

export function FileReadRenderer({
  data,
  maxHeight = '400px',
}: FileReadRendererProps) {
  const t = useTranslations('designSystem.chat.fileReadRenderer');
  const { filePath, content, contentLength } = data;
  const isMarkdownFile = filePath?.toLowerCase().endsWith('.md') || false;
  const fileExtension = filePath?.split('.').pop()?.toUpperCase() || 'FILE';
  const fileName = filePath?.split('/').pop() || filePath || 'file';
  const skillName = filePath ? getSkillDisplayNameFromPath(filePath) : null;
  const looksLikeMarkdown =
    content &&
    (content.includes('##') ||
      content.includes('**') ||
      content.match(/^[-*]\s/m) ||
      content.match(/^\d+\.\s/m) ||
      content.includes('```'));
  const renderContent = () => {
    if (!content) {
      return (
        <Text fontSize="xs" color="fg.muted" fontFamily="mono">
          {'No content available'}
        </Text>
      );
    }

    if (isMarkdownFile || looksLikeMarkdown) {
      return <MarkdownPreview source={content} fontSize={12} compact />;
    }

    return (
      <Text
        fontSize="xs"
        fontFamily="mono"
        color="fg.default"
        whiteSpace="pre-wrap"
      >
        {content}
      </Text>
    );
  };

  return (
    <VStack alignItems="start" gap={2} width="100%">
      {skillName ? (
        <Box
          bg="bg.subtle"
          borderWidth="1px"
          borderColor="border.subtle"
          borderRadius="sm"
          px={2}
          py={1.5}
          width="100%"
        >
          <HStack gap={1.5} alignItems="center" minWidth={0}>
            <Box
              width="18px"
              height="18px"
              borderRadius="full"
              bg="bg.canvas"
              borderWidth="1px"
              borderColor="border.default"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="colorPalette.default"
              animationStyle="shimmer-text"
              flexShrink={0}
            >
              <SparklesIcon size={11} />
            </Box>
            <Text
              fontSize="xs"
              fontFamily="mono"
              fontWeight="medium"
              color="fg.default"
              animationStyle="shimmer-text"
              flexShrink={0}
            >
              {'Reading skill'}
            </Text>
            <Text
              fontSize="xs"
              fontFamily="mono"
              color="fg.muted"
              truncate
              maxWidth="520px"
            >
              {skillName}
            </Text>
          </HStack>
        </Box>
      ) : null}

      {!skillName ? (
        <>
          <HStack alignItems="baseline" gap={1.5} flex={1} minWidth={0}>
            <Text
              fontSize="xs"
              fontWeight="medium"
              fontFamily="mono"
              color="fg.default"
              flexShrink={0}
            >
              {'Read'}
            </Text>
            <Text
              fontSize="xs"
              color="fg.muted"
              fontFamily="mono"
              truncate
              maxWidth="520px"
            >
              {filePath ?? fileName}
            </Text>
          </HStack>

          <HStack gap={1.5} alignItems="center">
            <Text fontSize="2xs" color="fg.muted" fontFamily="mono">
              {fileExtension}
            </Text>
            {contentLength !== undefined && (
              <>
                <Text fontSize="2xs" color="fg.muted" fontFamily="mono">
                  •
                </Text>
                <Text fontSize="2xs" color="fg.muted" fontFamily="mono">
                  {t('contentLength', { count: contentLength })}
                </Text>
              </>
            )}
          </HStack>

          <Box
            bg="bg.subtle"
            p={2}
            borderRadius="sm"
            borderWidth="1px"
            borderColor="border.subtle"
            width="100%"
            maxHeight={maxHeight}
            overflow="auto"
            position="relative"
          >
            {renderContent()}
          </Box>
        </>
      ) : null}
    </VStack>
  );
}
